import React from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Checkbox, Chip, Stack, Typography } from '@mui/material';

import { pluralizeToString } from '~/utils/stringUtils';

import BlackoutDiffTable from './BlackoutDiffTable';
import ScopeCell from './ScopeCell';
import { ROOM_SCOPE_SOURCE_TOOLTIP, SCOPE_SOURCE_LABEL, getRoomsScopeChipColor } from './blackoutScopeClassifier';
import type { RoomTypeBlackoutPlan } from './buildBlackoutApplyPlan';

interface Props {
  changedPlans: Array<RoomTypeBlackoutPlan>;
  excludedRoomTypeIds: Set<string>;
  showScopeColumn: boolean;
  toggleRoomType: (roomTypeId: string, checked: boolean) => void;
}

export default function BlackoutApplyReviewTable({
  changedPlans,
  excludedRoomTypeIds,
  showScopeColumn,
  toggleRoomType,
}: Props) {
  if (changedPlans.length === 0) {
    return <Alert severity="info">All room rates already match this source. There is nothing to apply.</Alert>;
  }

  return (
    <Stack spacing={1}>
      {changedPlans.map((plan) => (
        <Accordion key={plan.roomTypeId} defaultExpanded={changedPlans.length <= 3}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" alignItems="center" gap={1} sx={{ width: '100%' }}>
              <Checkbox
                checked={!excludedRoomTypeIds.has(plan.roomTypeId)}
                onChange={(event) => toggleRoomType(plan.roomTypeId, event.target.checked)}
                onClick={(event) => event.stopPropagation()}
              />
              <Typography variant="body2">{plan.roomTypeName}</Typography>
              {showScopeColumn && (
                <ScopeCell
                  color={getRoomsScopeChipColor({
                    matchedRooms: [],
                    scope: plan.scopeSource === 'needs_review' ? 'only' : 'all',
                    source: plan.scopeSource,
                  })}
                  tooltip={ROOM_SCOPE_SOURCE_TOOLTIP[plan.scopeSource]}
                  value={SCOPE_SOURCE_LABEL[plan.scopeSource]}
                />
              )}
              <Chip
                size="small"
                label={`${pluralizeToString('range', plan.rangesToCreate.length)} to create, ${pluralizeToString('block', plan.blocksToDelete.length)} to remove`}
                color="warning"
                variant="outlined"
                sx={{ ml: 'auto' }}
              />
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <BlackoutDiffTable deletedRanges={plan.deletedRanges} rangesToCreate={plan.rangesToCreate} />
          </AccordionDetails>
        </Accordion>
      ))}
    </Stack>
  );
}
